import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { format } from "date-fns";
import { Loader2, X, ArrowUp, ArrowDown, ArrowUpDown, RefreshCw } from "lucide-react";
import { cn } from "@/utils/cn";
import { useMessageMonitor } from "@/services/messageMonitor";
import type { MessageMonitorResponse } from "@/types/messageMonitor";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayIso(): string {
  return format(new Date(), "yyyy-MM-dd");
}

function formatDateTime(iso: string): string {
  try {
    return format(new Date(iso), "MM/dd/yyyy HH:mm:ss");
  } catch {
    return iso;
  }
}

function tryFormatJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function truncate(s: string | null | undefined, max = 80): string {
  if (!s) return "";
  return s.length > max ? s.slice(0, max) + "…" : s;
}

// ─── Sort ─────────────────────────────────────────────────────────────────────

type SortField = "createdDate" | "direction" | "messageType" | "status" | "retryCount";
type SortDir = "asc" | "desc";

function sortMessages(
  items: MessageMonitorResponse[],
  field: SortField,
  dir: SortDir
): MessageMonitorResponse[] {
  return [...items].sort((a, b) => {
    let cmp: number;
    if (field === "retryCount") {
      cmp = a.retryCount - b.retryCount;
    } else {
      cmp = String(a[field]).localeCompare(String(b[field]));
    }
    return dir === "asc" ? cmp : -cmp;
  });
}

// ─── Status badge class ───────────────────────────────────────────────────────

function statusCls(status: string): string {
  switch (status.toLowerCase()) {
    case "success":
    case "processed":
      return "bg-green-100 text-green-800";
    case "failed":
    case "error":
      return "bg-red-100 text-red-700";
    case "pending":
    case "queued":
      return "bg-amber-100 text-amber-800";
    case "retry":
      return "bg-orange-100 text-orange-700";
    default:
      return "bg-slate-100 text-slate-700";
  }
}

// ─── Column definitions ───────────────────────────────────────────────────────

const COLUMNS: { key: SortField | "payload"; label: string; sortable: boolean; className?: string }[] = [
  { key: "createdDate", label: "Date / Time", sortable: true, className: "min-w-[140px]" },
  { key: "direction", label: "Direction", sortable: true, className: "w-24" },
  { key: "messageType", label: "Message Type", sortable: true, className: "min-w-[130px]" },
  { key: "status", label: "Status", sortable: true, className: "w-28" },
  { key: "payload", label: "Input / Payload", sortable: false, className: "min-w-[160px]" },
  { key: "payload", label: "Vendor Request", sortable: false, className: "min-w-[160px]" },
  { key: "payload", label: "Vendor Response", sortable: false, className: "min-w-[160px]" },
  { key: "payload", label: "Error Message", sortable: false, className: "min-w-[160px]" },
  { key: "retryCount", label: "Retries", sortable: true, className: "w-16 text-right" }
];

// ─── SortIcon ────────────────────────────────────────────────────────────────

function SortIcon({
  colKey,
  sortField,
  sortDir
}: {
  colKey: SortField | "payload";
  sortField: SortField;
  sortDir: SortDir;
}) {
  if (colKey === "payload" || sortField !== colKey)
    return <ArrowUpDown className="h-3 w-3 text-slate-400" />;
  return sortDir === "asc"
    ? <ArrowUp className="h-3 w-3" />
    : <ArrowDown className="h-3 w-3" />;
}

// ─── Payload modal ────────────────────────────────────────────────────────────

interface PayloadModalProps {
  title: string;
  content: string;
  onClose: () => void;
  /** Element to return focus to when the modal closes. */
  triggerRef: React.RefObject<HTMLElement | null>;
}

function PayloadModal({ title, content, onClose, triggerRef }: PayloadModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  // Auto-focus the close button when the modal mounts (WCAG 2.4.3)
  useEffect(() => {
    closeRef.current?.focus();
    return () => {
      // Return focus to the element that opened the modal (WCAG 2.4.3)
      triggerRef.current?.focus();
    };
  }, [triggerRef]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    // Focus trap: cycle Tab/Shift+Tab within the dialog
    if (e.key === "Tab") {
      const panel = e.currentTarget as HTMLElement;
      const focusable = panel.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    }
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
      aria-hidden="true"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="payload-modal-title"
        className="flex max-h-[80vh] w-[640px] flex-col rounded border border-slate-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 id="payload-modal-title" className="text-sm font-semibold text-slate-900">
            {title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-slate-500 hover:text-slate-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Payload — rendered as pre-formatted text, never as HTML */}
        <div className="flex-1 overflow-auto px-4 py-4">
          <pre className="whitespace-pre-wrap break-all font-mono text-xs text-slate-800">
            {content}
          </pre>
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-slate-200 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="border border-slate-300 px-4 py-1.5 text-sm hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function MessageMonitorPage() {
  const today = todayIso();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  // Committed dates — only changes when "Apply Filter" is clicked
  const [committedStart, setCommittedStart] = useState(today);
  const [committedEnd, setCommittedEnd] = useState(today);

  const [sortField, setSortField] = useState<SortField>("createdDate");
  const [sortDir, setSortDir] = useState<SortDir>("desc"); // newest first by default

  const [payloadModal, setPayloadModal] = useState<{ title: string; content: string } | null>(null);
  const payloadTriggerRef = useRef<HTMLElement | null>(null);

  const [dateError, setDateError] = useState<string | null>(null);

  // ── Data ────────────────────────────────────────────────────────────────────

  const { data: messages = [], isPending, isFetching, refetch } =
    useMessageMonitor(committedStart, committedEnd);

  const sorted = useMemo(
    () => sortMessages(messages, sortField, sortDir),
    [messages, sortField, sortDir]
  );

  // ── Handlers ────────────────────────────────────────────────────────────────

  function handleApply() {
    if (startDate > endDate) {
      setDateError("Start date must be on or before end date.");
      return;
    }
    setDateError(null);
    setCommittedStart(startDate);
    setCommittedEnd(endDate);
  }

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  const openPayload = useCallback(
    (title: string, raw: string | null | undefined, trigger: HTMLElement) => {
      if (!raw) return;
      payloadTriggerRef.current = trigger;
      setPayloadModal({ title, content: tryFormatJson(raw) });
    },
    []
  );

  function ariaSort(key: SortField | "payload") {
    if (key === "payload" || sortField !== key) return "none" as const;
    return (sortDir === "asc" ? "ascending" : "descending") as "ascending" | "descending";
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <main className="flex flex-col" aria-label="Message Monitor">
      {/* ── Header ── */}
      <div className="border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-base font-semibold text-slate-900">Message Monitor</h1>

          <div className="flex flex-wrap items-center gap-2 ml-auto">
            {/* Date range */}
            <div className="flex items-center gap-2">
              <label htmlFor="mm-start" className="text-xs font-medium text-slate-600">
                Date Range:
              </label>
              <label htmlFor="mm-start" className="text-xs font-medium text-slate-600 sr-only">
                Start date
              </label>
              <input
                id="mm-start"
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setDateError(null);
                }}
                className="rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:border-slate-500"
                aria-label="Start date"
              />
              <span className="text-xs text-slate-500">to</span>
              <label htmlFor="mm-end" className="sr-only">
                End date
              </label>
              <input
                id="mm-end"
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setDateError(null);
                }}
                className="rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:border-slate-500"
                aria-label="End date"
              />
              <button
                type="button"
                onClick={handleApply}
                className="rounded border border-slate-900 bg-slate-900 px-3 py-1 text-xs font-medium text-white hover:bg-slate-800"
              >
                Apply Filter
              </button>
            </div>

            {/* Refresh */}
            <button
              type="button"
              onClick={() => void refetch()}
              aria-label="Refresh"
              className="flex h-7 w-7 items-center justify-center rounded border border-slate-300 hover:bg-slate-100"
            >
              <RefreshCw className={cn("h-3.5 w-3.5 text-slate-600", isFetching && "animate-spin")} />
            </button>
          </div>
        </div>

        {dateError && (
          <p role="alert" className="mt-1 text-xs text-red-600">{dateError}</p>
        )}
      </div>

      {/* ── Status bar ── */}
      <div className="border-b border-slate-100 bg-slate-50 px-4 py-1.5 text-xs text-slate-600">
        {isPending
          ? "Loading…"
          : `${sorted.length} message${sorted.length !== 1 ? "s" : ""} — ${committedStart} to ${committedEnd}`}
      </div>

      {/* ── Content ── */}
      {isPending ? (
        <div className="flex flex-1 items-center justify-center py-20" role="status" aria-live="polite" aria-label="Loading messages">
          <Loader2 className="h-7 w-7 animate-spin text-slate-400" />
          <span className="ml-3 text-sm text-slate-500">Loading messages…</span>
        </div>
      ) : sorted.length === 0 ? (
        <div className="flex flex-1 items-center justify-center py-20">
          <p className="text-sm text-slate-500">No messages found for the selected date range.</p>
        </div>
      ) : (
        <div
          className="overflow-auto"
          style={{ maxHeight: "calc(100vh - 130px)" }}
          role="region"
          aria-label="Message monitor grid"
        >
          <table className="w-full border-collapse text-sm" aria-label="Messages">
            {/* Header: #E8E8E8 matches Blazor Telerik grid header */}
            <thead className="sticky top-0 z-10 bg-[#E8E8E8]">
              <tr className="border-b border-slate-300">
                {COLUMNS.map((col, i) => (
                  <th
                    key={`${col.key}-${i}`}
                    scope="col"
                    className={cn(
                      "px-3 py-2 text-left text-xs font-semibold text-black",
                      col.className
                    )}
                    aria-sort={ariaSort(col.key)}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => handleSort(col.key as SortField)}
                        className="inline-flex items-center gap-1 hover:opacity-70"
                      >
                        {col.label}
                        <SortIcon
                          colKey={col.key}
                          sortField={sortField}
                          sortDir={sortDir}
                        />
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {sorted.map((msg, idx) => (
                <tr
                  key={msg.id}
                  /* Alternate rows: #F2F2F2 matches Blazor Telerik grid */
                  className={cn(
                    "border-b border-slate-200",
                    idx % 2 === 0 ? "bg-white" : "bg-[#F2F2F2]"
                  )}
                >
                  <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                    {formatDateTime(msg.createdDate)}
                  </td>

                  <td className="px-3 py-2">
                    <span
                      className={cn(
                        "rounded px-2 py-0.5 text-xs font-medium",
                        msg.direction === "Inbox"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-green-100 text-green-800"
                      )}
                    >
                      {msg.direction}
                    </span>
                  </td>

                  <td className="px-3 py-2 text-slate-700">{msg.messageType}</td>

                  <td className="px-3 py-2">
                    <span className={cn("rounded px-2 py-0.5 text-xs font-medium", statusCls(msg.status))}>
                      {msg.status}
                    </span>
                  </td>

                  {/* Input Payload */}
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={(e) => openPayload(`Input Payload — ${msg.messageType}`, msg.inputPayload, e.currentTarget)}
                      className="max-w-[200px] truncate text-left text-xs text-slate-600 underline hover:text-slate-900"
                      title={msg.inputPayload}
                    >
                      {truncate(msg.inputPayload)}
                    </button>
                  </td>

                  {/* Vendor Request */}
                  <td className="px-3 py-2">
                    {msg.vendorRequestPayload ? (
                      <button
                        type="button"
                        onClick={(e) => openPayload(`Vendor Request — ${msg.messageType}`, msg.vendorRequestPayload, e.currentTarget)}
                        className="max-w-[200px] truncate text-left text-xs text-slate-600 underline hover:text-slate-900"
                        title={msg.vendorRequestPayload}
                      >
                        {truncate(msg.vendorRequestPayload)}
                      </button>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>

                  {/* Vendor Response */}
                  <td className="px-3 py-2">
                    {msg.vendorResponsePayload ? (
                      <button
                        type="button"
                        onClick={(e) => openPayload(`Vendor Response — ${msg.messageType}`, msg.vendorResponsePayload, e.currentTarget)}
                        className="max-w-[200px] truncate text-left text-xs text-slate-600 underline hover:text-slate-900"
                        title={msg.vendorResponsePayload}
                      >
                        {truncate(msg.vendorResponsePayload)}
                      </button>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>

                  {/* Error Message */}
                  <td className="px-3 py-2">
                    {msg.errorMessage ? (
                      <span className="text-xs text-red-600" title={msg.errorMessage}>
                        {truncate(msg.errorMessage, 50)}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>

                  <td className="px-3 py-2 text-right text-slate-700">{msg.retryCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Payload modal ── */}
      {payloadModal && (
        <PayloadModal
          title={payloadModal.title}
          content={payloadModal.content}
          onClose={() => setPayloadModal(null)}
          triggerRef={payloadTriggerRef}
        />
      )}
    </main>
  );
}
